import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CopyButton } from '../fileProcessing/FileCardBase';

interface SimpleEditableFieldProps {
  label: string;
  value: string;
  fieldType: 'title' | 'description' | string; // Allow custom field types
  fileId: string;
  onSave: (fileId: string, metadata: { [key: string]: string }) => Promise<void>;
  maxLength?: number;
  minRows?: number;
  maxRows?: number;
  placeholder?: string;
  disabled?: boolean;
}

export const SimpleEditableField: React.FC<SimpleEditableFieldProps> = ({
  label,
  value,
  fieldType,
  fileId,
  onSave,
  maxLength = fieldType === 'title' ? 150 : 300,
  minRows = fieldType === 'title' ? 1 : 3,
  maxRows = fieldType === 'title' ? 1 : 5,
  placeholder = `Enter ${fieldType}...`,
  disabled = false
}) => {
  const [localValue, setLocalValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update local value when prop value changes (for regeneration)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Auto-resize textarea (disabled for single-line title)
  const adjustTextareaHeight = useCallback(() => {
    if (fieldType === 'title') return; // Don't auto-resize title field

    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
    const maxHeight = lineHeight * maxRows;

    textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
  }, [maxRows, fieldType]);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    // Enforce max length
    if (newValue.length > maxLength) return;

    setLocalValue(newValue);
    adjustTextareaHeight();
  };

  // Handle save on blur
  const handleSave = () => {
    if (localValue !== value) {
      onSave(fileId, { [fieldType]: localValue });
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && fieldType === 'title') {
      e.preventDefault();
      handleSave();
      textareaRef.current?.blur();
    }
  };

  const isNearLimit = localValue.length > maxLength * 0.8;

  return (
    <div className="space-y-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <label className="text-xs font-medium text-gray-700">
            {label}
          </label>
        </div>

        <div className="flex items-center space-x-3">
          {/* Character Count */}
          <span className="text-xs text-gray-500">
            {localValue.length}
          </span>

          {/* Copy Button */}
          <CopyButton content={localValue} />
        </div>
      </div>

      {/* Input Field */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={handleChange}
          onBlur={handleSave}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-hidden"
          style={{
            lineHeight: '1.25',
            minHeight: fieldType === 'description' ? '3rem' : 'auto',
            height: 'auto',
            whiteSpace: fieldType === 'description' ? 'normal' : 'nowrap'
          }}
        />
      </div>
    </div>
  );
};

export default SimpleEditableField;
